import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router'
import { api } from '../utils/api'
import { formatDate, formatTime } from '../utils/helpers'
import ThemeToggle from '../components/ThemeToggle'
import ReactMarkdown from 'react-markdown'
import { withLineBreaks } from '../utils/markdown'
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'

const EmailResponse = () => {
  const { token, meetupId, action } = useParams()
  const [loading, setLoading] = useState(true)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    const handleResponse = async () => {
      try {
        const response = await api.get(`/users/respond/${token}/${meetupId}/${action}`)
        setResult(response.data)
      } catch (error) {
        setError(error.response?.data?.error || 'Er is een fout opgetreden')
      } finally {
        setLoading(false)
      }
    }

    handleResponse()
  }, [token, meetupId, action])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="absolute top-4 right-4">
          <ThemeToggle />
        </div>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Je response wordt verwerkt...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="absolute top-4 right-4">
          <ThemeToggle />
        </div>
        <div className="max-w-md w-full text-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
            <div className="text-6xl mb-4">❌</div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Oeps, er ging iets mis
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {error}
            </p>
          </div>
        </div>
      </div>
    )
  }

  const isConfirm = action === 'confirm'
  const meetup = result?.meetup

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <div className="max-w-2xl w-full">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className={`px-6 py-8 text-center ${isConfirm ? 'bg-green-50 dark:bg-green-900/30' : 'bg-red-50 dark:bg-red-900/30'}`}>
            <div className="text-6xl mb-4">
              {isConfirm ? '✅' : '❌'}
            </div>
            <h1 className={`text-2xl font-bold mb-2 ${isConfirm ? 'text-green-900 dark:text-green-100' : 'text-red-900 dark:text-red-100'}`}>
              {isConfirm ? 'Je komt!' : 'Afgemeld'}
            </h1>
            <p className={`text-lg ${isConfirm ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
              {result.message}
            </p>
          </div>

          {/* Meetup Details */}
          {meetup && (
            <div className="px-6 py-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Meetup Details
              </h2>

              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3">
                <h3 className="font-medium text-gray-900 dark:text-white">
                  {meetup.title}
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-600 dark:text-gray-400">
                  <div>
                    <span className="font-medium">Datum:</span><br />
                    {formatDate(meetup.date)}
                  </div>
                  <div>
                    <span className="font-medium">Tijd:</span><br />
                    {formatTime(meetup.startTime)} - {formatTime(meetup.endTime)}
                  </div>
                  <div className="sm:col-span-2">
                    <span className="font-medium">Locatie:</span><br />
                    {meetup.location}
                  </div>
                </div>

                {meetup.description && (
                  <div className="pt-3 border-t border-gray-200 dark:border-gray-600">
                    <span className="font-medium text-gray-900 dark:text-white mb-2 block">Beschrijving:</span>
                    <div className="markdown-body text-sm bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-300 dark:border-gray-400 dark:**:text-white! dark:[&_a]:text-primary-400! dark:[&_code]:bg-gray-600! dark:[&_pre]:bg-gray-600!">
                      <ReactMarkdown
                        components={{
                          a: ({ node, children, href, ...props }) => (
                            <a href={href} target="_blank" rel="noopener noreferrer" {...props} className="inline-flex items-center gap-1">
                              {children}
                              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 inline-block" />
                            </a>
                          )
                        }}
                      >
                        {withLineBreaks(meetup.description)}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Status info */}
          {isConfirm && result.status && (
            <div className="px-6 pb-6">
              <div className={`rounded-lg p-4 ${
                result.status === 'confirmed'
                  ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800'
                  : 'bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800'
              }`}>
                <div className="flex items-center">
                  <span className="text-lg mr-2">
                    {result.status === 'confirmed' ? '🎉' : '⏳'}
                  </span>
                  <div>
                    <p className={`font-medium ${
                      result.status === 'confirmed' ? 'text-green-800 dark:text-green-200' : 'text-yellow-800 dark:text-yellow-200'
                    }`}>
                      {result.status === 'confirmed'
                        ? 'Je plek is bevestigd!'
                        : 'Je staat op de wachtlijst'
                      }
                    </p>
                    <p className={`text-sm ${
                      result.status === 'confirmed' ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'
                    }`}>
                      {result.status === 'confirmed'
                        ? 'Zien we je daar!'
                        : 'We laten je weten als er een plek vrijkomt.'
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="px-6 py-6 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              <button
                onClick={() => window.close()}
                className="hover:text-gray-700 dark:hover:text-gray-200 hover:underline cursor-pointer transition-colors"
              >
                Je kunt dit venster sluiten.
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EmailResponse